import { RootFiIntegration } from '@prisma/client';

import { ROOTFI_INTEGRATION_ENUM_TO_TEXT } from '@commenda/utils';
import {
  Marketplace,
  TransactionStatus,
} from '../new-sales-tax/transactions/types';

export enum TransactionSourcePlatform {
  RAZORPAY = 'RAZORPAY',
  ZOHO_BOOKS = 'ZOHO_BOOKS',
  QUICKBOOKS_SANDBOX = 'QUICKBOOKS_SANDBOX',
  QUICKBOOKS = 'QUICKBOOKS',
  XERO = 'XERO',
  TALLY = 'TALLY',
  SAGE_CLOUD_ACCOUNTING = 'SAGE_CLOUD_ACCOUNTING',
  MS_DYNAMICS_365 = 'MS_DYNAMICS_365',
  SAGE_ZA_CLOUD_ACCOUNTING = 'SAGE_ZA_CLOUD_ACCOUNTING',
  MYOB_BUSINESS = 'MYOB_BUSINESS',
  WAVE = 'WAVE',
  ODOO_ACCOUNTING = 'ODOO_ACCOUNTING',
  NETSUITE = 'NETSUITE',
  WAFEQ = 'WAFEQ',
  FRESHBOOKS = 'FRESHBOOKS',
  MEKARI_JURNAL = 'MEKARI_JURNAL',
  BUSY = 'BUSY',
  ROOTFI_SANDBOX = 'ROOTFI_SANDBOX',
  QOYOD = 'QOYOD',
  SHOPIFY = 'SHOPIFY',
  STRIPE = 'STRIPE',
  PAYPAL = 'PAYPAL',
  HUBSPOT = 'HUBSPOT',
  ZOHO_CRM = 'ZOHO_CRM',
  PIPEDRIVE = 'PIPEDRIVE',
  WOOCOMMERCE = 'WOOCOMMERCE',
  BIGCOMMERCE = 'BIGCOMMERCE',
  AMAZON = 'AMAZON',
  RILLET = 'RILLET',
  RILLET_SANDBOX = 'RILLET_SANDBOX',
  PUZZLE = 'PUZZLE',
  PUZZLE_SANDBOX = 'PUZZLE_SANDBOX',
  EPICOR_PROPHET_21 = 'EPICOR_PROPHET_21',
  OTHER = 'OTHER',
}

export function integrationTypeDisplayName(
  integration: RootFiIntegration
): string {
  const label = ROOTFI_INTEGRATION_ENUM_TO_TEXT[integration] ?? undefined;
  if (typeof label === 'string') {
    return label;
  }
  return integration
    .replaceAll('_', ' ')
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export enum ActivityType {
  REMITTANCE = 'REMITTANCE',
  NEXUS_TRIGGERED = 'NEXUS_TRIGGERED',
  APPROACHING_NEXUS = 'APPROACHING_NEXUS',
  SOME_ACTIVITY = 'SOME_ACTIVITY',
}

export enum NexusStatus {
  NexusTriggered = 'NexusTriggered',
  ApproachingNexus = 'ApproachingNexus',
  NoNexus = 'NoNexus',
  Remittance = 'Remittance',
}

export enum SalesTaxTransactionsActivityType {
  TOTAL_TRANSACTIONS = 'TOTAL_TRANSACTIONS',
  ESTIMATED_TOTAL_SALES = 'ESTIMATED_TOTAL_SALES',
  ESTIMATED_TAX_COLLECTED = 'ESTIMATED_TAX_COLLECTED',
  JURISDICTIONS = 'JURISDICTIONS',
  ESTIMATED_TAXABLE_AMOUNT = 'ESTIMATED_TAXABLE_AMOUNT',
  ESTIMATED_NON_TAXABLE_AMOUNT = 'ESTIMATED_NON_TAXABLE_AMOUNT',
  ESTIMATED_EXEMPT_AMOUNT = 'ESTIMATED_EXEMPT_AMOUNT',
  ESTIMATED_MARKETPLACE_AMOUNT = 'ESTIMATED_MARKETPLACE_AMOUNT',
  TOTAL_SALES = 'TOTAL_SALES',
  TAX_COLLECTED = 'TAX_COLLECTED',
  TAXABLE_AMOUNT = 'TAXABLE_AMOUNT',
  NON_TAXABLE_AMOUNT = 'NON_TAXABLE_AMOUNT',
  TAX_AMOUNT = 'TAX_AMOUNT',
  INVOICE_COUNT = 'INVOICE_COUNT',
}

export enum SalesTaxReturnsActivityType {
  FILED = 'FILED',
  PENDING = 'PENDING',
  OVERDUE = 'OVERDUE',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
}

export type SalesTaxOverviewData<T> = {
  type: T;
  activityTitle: string;
  value: string | number;
};

export enum SalesTaxTaskStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Overdue = 'Overdue',
}

export enum RemittingStatus {
  Completed = 'Completed',
  Overdue = 'Overdue',
  Upcoming = 'Upcoming',
  Due = 'Due',
  Filed = 'Filed',
  Unfiled = 'Unfiled',
}

export const TransactionStatusText: Record<TransactionStatus, string> = {
  [TransactionStatus.UNCOMMITTED]: 'Recorded',
  [TransactionStatus.COMMITTED]: 'To be filed',
  [TransactionStatus.VOIDED]: 'Void',
  [TransactionStatus.LOCKED]: 'Locked',
};

export const MarketplaceNameMapping: Record<Marketplace, string> = {
  [Marketplace.AMAZON]: 'Amazon',
};

export type SupportedDateFormatforCsvMapping =
  | 'YYYY-MM-DD'
  | 'YYYY/MM/DD'
  | 'YYYY-DD-MM'
  | 'YYYY/DD/MM'
  | 'DD-MM-YYYY'
  | 'DD/MM/YYYY'
  | 'MM-DD-YYYY'
  | 'MM/DD/YYYY'
  | 'M-D-YYYY'
  | 'D-M-YYYY'
  | 'M/D/YYYY'
  | 'D/M/YYYY'
  | 'Y-D-M'
  | 'Y/D/M'
  | 'YY-MM-DD'
  | 'YY/MM/DD'
  | 'YY-DD-MM'
  | 'YY/DD/MM'
  | 'DD-MM-YY'
  | 'DD/MM/YY'
  | 'MM-DD-YY'
  | 'MM/DD/YY'
  | 'M-D-YY'
  | 'D-M-YY'
  | 'M/D/YY'
  | 'D/M/YY'
  | 'YY-D-M'
  | 'YY/D/M';

enum SalesTaxRegistrationTaxTypeName {
  BUSINESS_AND_OCCUPATION_TAX = 'BUSINESS_AND_OCCUPATION_TAX',
  DISTRICT_USE_TAX = 'DISTRICT_USE_TAX',
  GENERAL_EXCISE_TAX = 'GENERAL_EXCISE_TAX',
  GROSS_RECEIPTS_TAX = 'GROSS_RECEIPTS_TAX',
  REMOTE_SELLERS_SALES_TAX = 'REMOTE_SELLERS_SALES_TAX',
  REMOTE_SELLERS_SALES_AND_USE_TAX = 'REMOTE_SELLERS_SALES_AND_USE_TAX',
  REMOTE_TRANSACTION_PRIVILEGE_TAX = 'REMOTE_TRANSACTION_PRIVILEGE_TAX',
  RETAIL_DELIVERY_FEE = 'RETAIL_DELIVERY_FEE',
  RETAIL_SALES_TAX = 'RETAIL_SALES_TAX',
  RETAIL_SALES_AND_USE_TAX = 'RETAIL_SALES_AND_USE_TAX',
  RETAIL_USE_TAX = 'RETAIL_USE_TAX',
  RETAILER_SALES_TAX = 'RETAILER_SALES_TAX',
  RETAILER_USE_TAX = 'RETAILER_USE_TAX',
  RETAILERS_OCCUPATION_TAX = 'RETAILERS_OCCUPATION_TAX',
  SALES_AND_USE_TAX = 'SALES_AND_USE_TAX',
  SELLERS_USE_TAX = 'SELLERS_USE_TAX',
  SIMPLIFIED_SELLERS_USE_TAX = 'SIMPLIFIED_SELLERS_USE_TAX',
  TRANSACTION_PRIVILEGE_TAX = 'TRANSACTION_PRIVILEGE_TAX',
  VENDOR_USE_TAX = 'VENDOR_USE_TAX',
  VENDORS_USE_TAX = 'VENDORS_USE_TAX',
  OUT_OF_STATE_DEALERS_SALES_AND_USE_TAX = 'OUT_OF_STATE_DEALERS_SALES_AND_USE_TAX',
  VENDORS_USE = 'VENDORS_USE',
  RETAIL_SALES = 'RETAIL_SALES',
  RETAILERS_COMPENSATING_USE_TAX = 'RETAILERS_COMPENSATING_USE_TAX',
}

export enum SalesTaxRegistrationTaxTypeCode {
  RSST = 'RSST',
  RST = 'RST',
  RUT = 'RUT',
  SSUT = 'SSUT',
  GRT = 'GRT',
  VUT = 'VUT',
  RTPT = 'RTPT',
  TPT = 'TPT',
  DTT = 'DTT',
  RDF = 'RDF',
  SUT = 'SUT',
  GET = 'GET',
  ROT = 'ROT',
  CUT = 'CUT',
  RSSUT = 'RSSUT',
  BAO = 'BAO',
}

export const SalesTaxRegistrationTaxNameAndCodeMapping: Record<
  SalesTaxRegistrationTaxTypeName,
  SalesTaxRegistrationTaxTypeCode
> = {
  [SalesTaxRegistrationTaxTypeName.REMOTE_SELLERS_SALES_TAX]:
    SalesTaxRegistrationTaxTypeCode.RSST,
  [SalesTaxRegistrationTaxTypeName.RETAILER_SALES_TAX]:
    SalesTaxRegistrationTaxTypeCode.RST,
  [SalesTaxRegistrationTaxTypeName.SELLERS_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.RUT,
  [SalesTaxRegistrationTaxTypeName.SIMPLIFIED_SELLERS_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.SSUT,
  [SalesTaxRegistrationTaxTypeName.GROSS_RECEIPTS_TAX]:
    SalesTaxRegistrationTaxTypeCode.GRT,
  [SalesTaxRegistrationTaxTypeName.VENDOR_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.VUT,
  [SalesTaxRegistrationTaxTypeName.REMOTE_TRANSACTION_PRIVILEGE_TAX]:
    SalesTaxRegistrationTaxTypeCode.RTPT,
  [SalesTaxRegistrationTaxTypeName.TRANSACTION_PRIVILEGE_TAX]:
    SalesTaxRegistrationTaxTypeCode.TPT,
  [SalesTaxRegistrationTaxTypeName.DISTRICT_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.DTT,
  [SalesTaxRegistrationTaxTypeName.RETAIL_DELIVERY_FEE]:
    SalesTaxRegistrationTaxTypeCode.RDF,
  [SalesTaxRegistrationTaxTypeName.RETAIL_SALES_TAX]:
    SalesTaxRegistrationTaxTypeCode.RST,
  [SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.RST,
  [SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.SUT,
  [SalesTaxRegistrationTaxTypeName.GENERAL_EXCISE_TAX]:
    SalesTaxRegistrationTaxTypeCode.GET,
  [SalesTaxRegistrationTaxTypeName.RETAILERS_OCCUPATION_TAX]:
    SalesTaxRegistrationTaxTypeCode.ROT,
  [SalesTaxRegistrationTaxTypeName.REMOTE_SELLERS_SALES_AND_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.RSSUT,
  [SalesTaxRegistrationTaxTypeName.BUSINESS_AND_OCCUPATION_TAX]:
    SalesTaxRegistrationTaxTypeCode.BAO,
  [SalesTaxRegistrationTaxTypeName.OUT_OF_STATE_DEALERS_SALES_AND_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.RUT,
  [SalesTaxRegistrationTaxTypeName.VENDORS_USE]:
    SalesTaxRegistrationTaxTypeCode.RUT,
  [SalesTaxRegistrationTaxTypeName.RETAIL_SALES]:
    SalesTaxRegistrationTaxTypeCode.RST,
  [SalesTaxRegistrationTaxTypeName.RETAIL_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.RUT,
  [SalesTaxRegistrationTaxTypeName.RETAILERS_COMPENSATING_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.CUT,
  [SalesTaxRegistrationTaxTypeName.RETAILER_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.RUT,
  [SalesTaxRegistrationTaxTypeName.VENDORS_USE_TAX]:
    SalesTaxRegistrationTaxTypeCode.RUT,
};

export const SalesTaxRegistrationTaxTypeNameMap: Record<
  SalesTaxRegistrationTaxTypeName,
  string
> = {
  [SalesTaxRegistrationTaxTypeName.BUSINESS_AND_OCCUPATION_TAX]:
    'Business and Occupation Tax',
  [SalesTaxRegistrationTaxTypeName.DISTRICT_USE_TAX]: 'District Use Tax',
  [SalesTaxRegistrationTaxTypeName.GENERAL_EXCISE_TAX]: 'General Excise Tax',
  [SalesTaxRegistrationTaxTypeName.GROSS_RECEIPTS_TAX]: 'Gross Receipts Tax',
  [SalesTaxRegistrationTaxTypeName.REMOTE_SELLERS_SALES_TAX]:
    'Remote Sellers Sales Tax',
  [SalesTaxRegistrationTaxTypeName.REMOTE_SELLERS_SALES_AND_USE_TAX]:
    'Remote Sellers Sales and Use Tax',
  [SalesTaxRegistrationTaxTypeName.REMOTE_TRANSACTION_PRIVILEGE_TAX]:
    'Remote Transaction Privilege Tax',
  [SalesTaxRegistrationTaxTypeName.RETAIL_DELIVERY_FEE]: 'Retail Delivery Fee',
  [SalesTaxRegistrationTaxTypeName.RETAIL_SALES_TAX]: 'Retail Sales Tax',
  [SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX]:
    'Retail Sales and Use Tax',
  [SalesTaxRegistrationTaxTypeName.RETAIL_USE_TAX]: 'Retail Use Tax',
  [SalesTaxRegistrationTaxTypeName.RETAILER_SALES_TAX]: 'Retailer Sales Tax',
  [SalesTaxRegistrationTaxTypeName.RETAILER_USE_TAX]: 'Retailer Use Tax',
  [SalesTaxRegistrationTaxTypeName.RETAILERS_OCCUPATION_TAX]:
    'Retailers Occupation Tax',
  [SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX]: 'Sales and Use Tax',
  [SalesTaxRegistrationTaxTypeName.SELLERS_USE_TAX]: 'Sellers Use Tax',
  [SalesTaxRegistrationTaxTypeName.SIMPLIFIED_SELLERS_USE_TAX]:
    'Simplified Sellers Use Tax',
  [SalesTaxRegistrationTaxTypeName.TRANSACTION_PRIVILEGE_TAX]:
    'Transaction Privilege Tax',
  [SalesTaxRegistrationTaxTypeName.VENDOR_USE_TAX]: 'Vendor Use Tax',
  [SalesTaxRegistrationTaxTypeName.OUT_OF_STATE_DEALERS_SALES_AND_USE_TAX]:
    "Out of State Dealer's Sales and Use Tax",
  [SalesTaxRegistrationTaxTypeName.VENDORS_USE]: 'Vendors Use',
  [SalesTaxRegistrationTaxTypeName.RETAIL_SALES]: 'Retail Sales',
  [SalesTaxRegistrationTaxTypeName.RETAILERS_COMPENSATING_USE_TAX]:
    'Retailers Compensating Use Tax',
  [SalesTaxRegistrationTaxTypeName.VENDORS_USE_TAX]: 'Vendors Use Tax',
};

export enum SalesTaxRegistrationStateAndTaxTypeMapping {
  // Alabama
  AL_SSUT = SalesTaxRegistrationTaxTypeName.SIMPLIFIED_SELLERS_USE_TAX,
  AL_RUT = SalesTaxRegistrationTaxTypeName.SELLERS_USE_TAX,
  AL_RST = SalesTaxRegistrationTaxTypeName.RETAILER_SALES_TAX,

  // Alaska
  AK_RSST = SalesTaxRegistrationTaxTypeName.REMOTE_SELLERS_SALES_TAX,

  // Arizona
  AZ_RTPT = SalesTaxRegistrationTaxTypeName.REMOTE_TRANSACTION_PRIVILEGE_TAX,
  AZ_TPT = SalesTaxRegistrationTaxTypeName.TRANSACTION_PRIVILEGE_TAX,

  // Arkansas
  AR_VUT = SalesTaxRegistrationTaxTypeName.VENDOR_USE_TAX,
  AR_GRT = SalesTaxRegistrationTaxTypeName.GROSS_RECEIPTS_TAX,

  // California
  CA_RUT = SalesTaxRegistrationTaxTypeName.RETAILER_USE_TAX,
  CA_RST = SalesTaxRegistrationTaxTypeName.RETAILER_SALES_TAX,
  CA_DTT = SalesTaxRegistrationTaxTypeName.DISTRICT_USE_TAX,

  // Colorado
  CO_RUT = SalesTaxRegistrationTaxTypeName.RETAILER_USE_TAX,
  CO_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,
  CO_RDF = SalesTaxRegistrationTaxTypeName.RETAIL_DELIVERY_FEE,

  // Connecticut
  CT_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // District of Columbia
  DC_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Florida
  FL_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Georgia
  GA_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Hawaii
  HI_GET = SalesTaxRegistrationTaxTypeName.GENERAL_EXCISE_TAX,

  // Iowa
  IA_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Idaho
  ID_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Illinois
  IL_ROT = SalesTaxRegistrationTaxTypeName.RETAILERS_OCCUPATION_TAX,

  // Indiana
  IN_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Kansas
  KS_CUT = SalesTaxRegistrationTaxTypeName.RETAILERS_COMPENSATING_USE_TAX,
  KS_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,

  // Kentucky
  KY_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Louisiana
  LA_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,
  LA_RSSUT = SalesTaxRegistrationTaxTypeName.REMOTE_SELLERS_SALES_AND_USE_TAX,

  // Massachusetts
  MA_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Maryland
  MD_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Maine
  ME_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Michigan
  MI_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Minnesota
  MN_RDF = SalesTaxRegistrationTaxTypeName.RETAIL_DELIVERY_FEE,
  MN_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Missouri
  MO_RUT = SalesTaxRegistrationTaxTypeName.VENDORS_USE,
  MO_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,

  // Mississippi
  MS_RUT = SalesTaxRegistrationTaxTypeName.RETAIL_USE_TAX,
  MS_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,

  // North Carolina
  NC_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // North Dakota
  ND_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Nebraska
  NE_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // New Jersey
  NJ_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // New Mexico
  NM_GRT = SalesTaxRegistrationTaxTypeName.GROSS_RECEIPTS_TAX,

  // Nevada
  NV_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // New York
  NY_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Ohio
  OH_RUT = SalesTaxRegistrationTaxTypeName.RETAIL_USE_TAX,
  OH_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,

  // Oklahoma
  OK_RUT = SalesTaxRegistrationTaxTypeName.VENDORS_USE_TAX,
  OK_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,

  // Pennsylvania
  PA_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Rhode Island
  RI_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // South Carolina
  SC_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // South Dakota
  SD_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Tennessee
  TN_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,

  // Texas
  TX_SSUT = SalesTaxRegistrationTaxTypeName.SIMPLIFIED_SELLERS_USE_TAX,
  TX_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,
  TX_RUT = SalesTaxRegistrationTaxTypeName.RETAIL_USE_TAX,

  // Utah
  UT_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,

  // Virginia
  VA_RUT = SalesTaxRegistrationTaxTypeName.OUT_OF_STATE_DEALERS_SALES_AND_USE_TAX,
  VA_RST = SalesTaxRegistrationTaxTypeName.RETAIL_SALES_AND_USE_TAX,

  // Vermont
  VT_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Washington
  WA_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Wisconsin
  WI_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // West Virginia
  WV_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,

  // Wyoming
  WY_SUT = SalesTaxRegistrationTaxTypeName.SALES_AND_USE_TAX,
}

export enum TransactionTemplateFormatEnum {
  BASIC = 'basic',
  ADVANCED = 'advanced',
}

export enum TransactionTemplateImportTypeEnum {
  INVOICES = 'invoices',
  LINE_ITEMS = 'line-items',
}

export enum BulkUploadTabs {
  REQUIRED_COLUMNS = 'required-columns',
  OPTIONAL_COLUMNS = 'optional-columns',
  ADVANCED_COLUMNS = 'advanced-columns',
}

export enum TaxCalculationType {
  TAX_EXCLUSIVE = 'TAX_EXCLUSIVE',
  TAX_INCLUSIVE = 'TAX_INCLUSIVE',
}

export enum SalesTaxTransactionVisibleColumnEnum {
  TransactionId = 'id',
  TypeOfSale = 'type',
  Status = 'status',
  DestinationState = 'destinationState',
  TransactionDate = 'date',
  SourcePlatform = 'sourcePlatform',
  Amount = 'amount',
  TaxCollected = 'taxCollected',
  Total = 'total',
}

export enum SalesTaxProductVisibleColumnEnum {
  SKU = 'sku',
  Name = 'name',
  Description = 'description',
  TaxCode = 'taxCode',
  SourcePlatform = 'sourcePlatform',
}

export enum SalesTaxExemptCustomerVisibleColumnEnum {
  CustomerId = 'customerId',
  CustomerName = 'customerName',
  CertificateTypes = 'certificateTypes',
  ExemptStates = 'states',
  ExemptionStatus = 'exemptionData',
  ExpirationStatus = 'expirationData',
}

export enum SalesTaxExemptionJurisdictionVisibleColumnEnum {
  State = 'state',
  CertificateType = 'certificateType',
  TaxId = 'taxId',
  CertificateName = 'certificateName',
  ExemptionStatus = 'exemptionStatus',
  ExpirationDate = 'expirationDate',
  Action = 'action',
}
